import { ChevronLeft, ChevronRight } from "lucide-react";
import { dayjs } from "../lib/time";
import { useI18n } from "../lib/i18n";

export function playerWeekStart(value, timezone = "UTC") {
  const source = value ? dayjs(value) : dayjs().tz(timezone);
  return source.startOf("day").subtract((source.day() + 6) % 7, "day").format("YYYY-MM-DD");
}

export function playerWeekRange(value, timezone = "UTC") {
  const start = playerWeekStart(value, timezone);
  return { start, end: dayjs(start).add(6, "day").format("YYYY-MM-DD") };
}

export default function CarePlayerWeekNavigator({ value = "", timezone = "UTC", onChange, actions = null }) {
  const { t, lang } = useI18n();
  const range = playerWeekRange(value, timezone);
  const move = (weeks) => onChange?.(dayjs(range.start).add(weeks, "week").format("YYYY-MM-DD"));
  const label = `${dayjs(range.start).locale(lang).format("DD. MMM")} – ${dayjs(range.end).locale(lang).format("DD. MMM YYYY")}`;

  return <section className="pc-ops-panel box-border h-[112px] xl:h-[64px] px-3 py-2.5 grid grid-cols-[auto_minmax(0,1fr)] xl:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2"
    data-testid="care-player-week-navigator">
    <div className="min-w-[150px]" data-testid="care-player-week-left">
      <p className="text-[10px] uppercase tracking-[.13em]" style={{ color: "var(--pc-cyan)" }}>{t("weeklyCalendar")}</p>
      <p className="text-sm font-semibold mt-0.5" data-testid="care-player-week-label">{label}</p>
    </div>
    <div className="col-span-2 row-start-2 xl:col-span-1 xl:col-start-2 xl:row-start-1 min-w-0 flex flex-wrap items-center justify-center gap-2"
      data-testid="care-player-week-center">
      {actions && <div className="min-w-0 flex flex-wrap items-center justify-center gap-2"
        data-testid="care-player-week-context-actions">{actions}</div>}
    </div>
    <div className="col-start-2 row-start-1 xl:col-start-3 justify-self-end flex items-center gap-2 whitespace-nowrap"
      data-testid="care-player-week-right">
      <button type="button" data-testid="calendar-prev-week" className="pc-btn pc-btn-ghost flex items-center gap-1.5"
        style={{ padding: "7px 9px", fontSize: 12 }} onClick={() => move(-1)} title={t("previousWeek")}>
        <ChevronLeft size={14} /> <span className="hidden 2xl:inline">{t("previousWeek")}</span>
      </button>
      <button type="button" data-testid="calendar-this-week" className="pc-btn pc-btn-ghost"
        style={{ padding: "7px 9px", fontSize: 12 }} onClick={() => onChange?.(dayjs().tz(timezone).format("YYYY-MM-DD"))}>
        {t("thisWeek")}
      </button>
      <label className="sr-only" htmlFor="care-player-week-date">{t("jumpToDate")}</label>
      <input id="care-player-week-date" data-testid="calendar-date-jump" type="date" className="pc-input"
        value={value || range.start} onChange={(event) => onChange?.(event.target.value)} style={{ width: 145, padding: "7px 9px" }} />
      <button type="button" data-testid="calendar-next-week" className="pc-btn pc-btn-ghost flex items-center gap-1.5"
        style={{ padding: "7px 9px", fontSize: 12 }} onClick={() => move(1)} title={t("nextWeek")}>
        <span className="hidden 2xl:inline">{t("nextWeek")}</span> <ChevronRight size={14} />
      </button>
      <span className="hidden 2xl:inline text-[10px] ml-1" style={{ color: "var(--pc-muted)" }}>{timezone}</span>
    </div>
  </section>;
}
